import { isNil } from 'lodash';

import { WorkflowError } from '../../stores/table-store';
import { getNodeColumnDisplayName } from '../../stores/utils/column-display-names';

/**
 * Build the visible title of a validation error row in the ValidationPopover.
 * The column segment uses the human-readable column name (PSTUDIO-3174) rather
 * than the internal `colId` key, so both Agent Validator and Bot Compiler
 * errors read "Asset Content" instead of "richAssetContent".
 *
 * The row address LEADS the title (PSTUDIO-3990). It is what tells two entries
 * for one duplicated number apart, and it used to be appended as a
 * ` (table row N)` suffix — which the rendering then ate: the title goes into
 * `<ps-text-body truncate>` (`design-system/src/patterns/graph-node/src/
 * graph-node.wc.ts`), and `truncate` is `white-space: nowrap; overflow: hidden;
 * text-overflow: ellipsis` (`design-system/src/components/text/text.scss`),
 * inside a popover 380px wide — or ~760/2 per column in the side-by-side
 * `agentCompilerMode === 'on'` layout, minus the flush variant's
 * `padding-inline`. An ellipsis eats the TAIL, so with a long column name the
 * two entries read identically again and the complaint the discriminator
 * exists for came straight back. A screen reader was unaffected (the full text
 * is in `aria-label`), i.e. the regression hit sighted users only.
 *
 * Two alternatives were rejected on the code, not by preference. A second line
 * is not available for the LABEL: `ps-graph-node` renders it as
 * `<ps-text-body truncate>`, a single line by construction. (Its CAPTION can be
 * given more, via the `captionLines` property added in PSTUDIO-4119 — but that
 * is the caption, not this address.) A native `title` tooltip reveals the text
 * only on hover, so it cannot be the fix on its own — it is added on the host
 * as an EXTRA (see the popover), never as the way the address is conveyed.
 *
 * The words "table row" stay load-bearing: a bare number next to a node number
 * reads as a second node number.
 *
 * A solution-level error is bound to neither a node nor a row (both null): it
 * describes the whole solution, so the column segment is meaningless and is
 * dropped entirely. The agent-compiler tags such errors `category: 'solution'`
 * and attaches them to the solution ref-file, yet still passes a `propertyName`
 * (e.g. "intent" for the missing NL entry node) for cell navigation — the title
 * must not surface that as a `Column: "…"` prefix (PSTUDIO-3575).
 */
export const buildErrorLabel = ({
  colId,
  nodeNumber,
  rowNumber,
}: Pick<WorkflowError, 'colId' | 'nodeNumber' | 'rowNumber'>): string => {
  // Solution-level error (no node, no row): title is just "Solution error",
  // never a column prefix (PSTUDIO-3575).
  if (isNil(nodeNumber) && isNil(rowNumber)) {
    return 'Solution error';
  }

  // `> 0`, not just "present and not NaN": the visible numbering is 1-based, and
  // a server error is built as `row_num ? row_num - 1 : null`, so `row_num: 1`
  // reaches here as 0. Printing `Table row: 0` addresses a row nobody can find
  // — the same false address as the phantom `Node: 0`.
  const hasRowNumber =
    !isNil(rowNumber) && !isNaN(rowNumber) && (rowNumber as number) > 0;

  // A KNOWN row number leads. A duplicate node number is, by its nature, one
  // number on two rows, so both popover entries rendered the identical
  // `Node: 2` — the user saw the same line twice for what reads as a single
  // problem. The two entries are not redundant: each addresses its own cell and
  // each is separately clickable (that is what makes BOTH offending cells
  // reachable), so they are made distinguishable rather than collapsed —
  // collapsing the list would silently drop click-navigation to the second
  // cell.
  const rowSegment = hasRowNumber ? `Table row: ${rowNumber}` : null;

  // When the row number is present-but-unusable (NaN) AND there is no node
  // number, the title still has to say that the row is unknown rather than
  // shrink to a bare column name — so that one case keeps the segment in its
  // original trailing position instead of leading with a non-address.
  const nodeSegment = isNil(nodeNumber)
    ? hasRowNumber
      ? null
      : 'Table row: No info about the row number'
    : `Node: ${nodeNumber}`;

  const columnSegment = colId
    ? `Column: "${getNodeColumnDisplayName(colId)}"`
    : null;

  return [rowSegment, columnSegment, nodeSegment]
    .filter((segment): segment is string => !!segment)
    .join(' - ');
};

/**
 * The row number to PRINT for an error — resolved from the row id, at render
 * time (PSTUDIO-3990).
 *
 * Why here and not on the error itself: the node-number errors are derived
 * inside a MobX computed that runs on every keystroke, and the visible row
 * number comes from `displayNumberById` → `getAllSortedRows()`. Storing it on
 * the error therefore made the whole sorted row list a dependency of a
 * derivation that only asks about node numbers — a drag-reorder, which changes
 * no number and no error, re-sorted 5,000 rows and re-derived on every `order`
 * write (~0.008 ms → ~8 ms), and `getRowDisplayNumber` being a `computedFn`
 * added one computed node per offending row per pass. The number is needed only while
 * the popover is OPEN, so it is resolved when it is rendered.
 *
 * ID-first, like every address since PSTUDIO-3991: `rowNumber` as carried by a
 * SERVER error is `row_num - 1`, a position in the payload, and a numberless
 * row has no number-keyed address at all. The carried value stays the fallback
 * for errors that have no `nodeId` (solution-level ones legitimately have
 * neither, and must keep reading as "Solution error").
 *
 * `getRowDisplayNumber` returns 0 for an id it does not know — a group header,
 * or a row that is gone — so 0 is treated as "unknown", not printed as row 0.
 * The SAME rule has to hold for the carried fallback: the visible numbering is
 * 1-based, and a server error is built as `row_num ? row_num - 1 : null`, so a
 * `row_num` of 1 arrives here as 0. Letting that through printed a title
 * reading `Table row: 0` — a row the user cannot find, and the same class of
 * false address as the phantom `Node: 0` this ticket exists to remove.
 */
export const resolveErrorRowNumber = (
  error: Pick<WorkflowError, 'nodeId' | 'rowNumber'>,
  getRowDisplayNumber: (rowId: string) => number
): number | null => {
  if (error.nodeId) {
    const displayNumber = getRowDisplayNumber(error.nodeId);
    if (displayNumber > 0) return displayNumber;
  }
  return error.rowNumber != null && error.rowNumber > 0
    ? error.rowNumber
    : null;
};

/**
 * The accessible name of a popover entry.
 *
 * `aria-label` on a host with `role="menuitem"` OVERRIDES the shadow-DOM
 * content, so the visible `label` is never announced on its own. Two entries
 * for one duplicated node number would therefore read identically to a screen
 * reader — exactly the problem the label's leading `Table row: N` segment
 * exists to solve (PSTUDIO-3990). Folding the address into the accessible name
 * is what makes the fix reach assistive technology and not only sighted users.
 *
 * The caption is what is folded in, not the raw message: the reason an entry is
 * disabled (the caller's `unreachableHint`) lives there, and a reason only a
 * sighted user can read is half a fix.
 */
export const buildErrorAccessibleName = (
  label: string,
  errorMessage: string | null | undefined,
  unknownErrorText: string
): string => `${label}. ${errorMessage ?? unknownErrorText}`;

/**
 * A translator, narrowed to what these helpers need.
 *
 * The strings are resolved by the CALLER and handed in, rather than reached for
 * from here: this module is deliberately importable by a unit test (
 * `validation-popover.tsx` is not — it pulls in Lit and crashes the node
 * environment), and pulling i18next in would put an initialised i18n instance
 * between every test and the logic it checks. Passing the resolver mirrors how
 * `deriveNodeNumberErrors` takes its column resolver as a parameter, and for
 * the same reason.
 */
export type TranslateFn = (
  key: string,
  options?: {
    defaultValue?: string;
    /** i18next picks the fallback by plural category too, so a counted string
     *  needs its singular spelled out — otherwise a missing key renders
     *  "1 errors". */
    defaultValue_one?: string;
    count?: number;
  }
) => string;

/**
 * Can this pair of indexes address a cell at all?
 *
 * Stated as what an index IS rather than as a list of what it is not. The list
 * form (null / undefined / NaN / ±Infinity) missed the value that actually
 * occurs: `getSortedRowIndex` is `visibleRowIndexById.get(id) ?? -1`, so `-1`
 * is its answer for "this row is not visible" — a collapsed group, an active
 * search, a row that is gone. Treated as an address it made the entry
 * CLICKABLE, and the click landed on `sortedRows[-1]`, i.e. did nothing
 * visible: exactly the silent no-op this ticket replaced with a stated reason.
 *
 * Moved out of the component so the "is this entry clickable?" decision is
 * pinned by a test: `validation-popover.tsx` cannot be imported by a unit test
 * (it pulls in Lit and crashes the node environment).
 */
const isUsableIndex = (value: unknown): boolean =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const isAddressableCell = (
  rowIndex: WorkflowError['rowIndex'],
  colIndex: WorkflowError['colIndex']
): boolean => isUsableIndex(rowIndex) && isUsableIndex(colIndex);

/**
 * Is this row one the table can actually scroll to — i.e. is it in
 * `getVisibleRows()`, the list `scrollToAndFocusCellById` searches?
 *
 * Mirrors that method's definition exactly: `getAllSortedRows()` minus `hidden`
 * rows, minus the rows an active search filtered out. `visibleRowIndexById` is
 * built from the same source and already skips `hidden` (`row-index-maps.ts`),
 * so the search set is the only extra term — and reading the precomputed map
 * rather than calling `getVisibleRows()` per entry keeps this O(1) per row,
 * matching the rule that row-position lookups are index reads.
 *
 * `searchResultIds` is `null` when no search is active. An EMPTY set is not the
 * same thing: a search that matched nothing makes `getVisibleRows()` return
 * `[]`, so nothing is reachable.
 */
export const buildReachableRowIdPredicate = ({
  visibleRowIndexById,
  searchResultIds,
}: {
  visibleRowIndexById: ReadonlyMap<string, number>;
  searchResultIds: ReadonlySet<string> | null;
}): ((rowId: string) => boolean) => {
  return (rowId: string) =>
    visibleRowIndexById.has(rowId) &&
    (searchResultIds === null || searchResultIds.has(rowId));
};

/**
 * Everything a popover entry needs, derived from one error — as a pure
 * function.
 *
 * Extracted because `validation-popover.tsx` is unreachable from a unit test:
 * importing it pulls in the Lit design system, which crashes the node
 * environment (proved by a top-level `throw` in that file leaving the entire
 * 190-file suite green). Every piece of wiring that lived only in the component
 * was therefore pinned by nothing — reverting `aria-label` from the composed
 * name back to the bare message, for instance, kept both the unit suite and the
 * e2e suite green, because every e2e assertion matches `aria-label` by a
 * substring of the MESSAGE. That is the half of this ticket that exists for
 * screen-reader users, so it needs a test that can fail.
 */
export interface ErrorRowContext {
  /** Position in the visible row list, or null when the row cannot be
   *  addressed; decides whether the entry is clickable. */
  resolveRowIndex: (error: WorkflowError) => number | null;
  /** id → the row number the user sees. `0`/absent means "unknown". */
  displayNumberById: ReadonlyMap<string, number>;
  /** Can the table scroll to this row at all? Optional: when the caller cannot
   *  answer, the entry keeps its pre-PSTUDIO-3990 behaviour rather than
   *  claiming a row is hidden. Built by `buildReachableRowIdPredicate`. */
  isRowReachable?: (rowId: string) => boolean;
  /** Why an unreachable entry cannot be opened — appended to its caption AND
   *  folded into its accessible name. Translated by the caller
   *  (`validation_popover.error_row.hidden_row_hint`). */
  unreachableHint: string;
  /** Stand-in for an error that arrived with no message at all, so the
   *  accessible name is never a bare title
   *  (`validation_popover.error_row.unknown_error`). */
  unknownErrorText: string;
}

/**
 * `clickable` — activating the entry navigates to the offending cell.
 * `unreachable` — the row exists but is not in the visible list (collapsed
 *  group, `hidden`, filtered out by an active search), so the click COULD not
 *  work; the entry is rendered disabled with the `unreachableHint` in its
 *  caption instead of silently doing nothing (PSTUDIO-3990 review defect 2).
 * `unaddressed` — the error carries no usable cell address at all (a server
 *  error with no indexes); the pre-existing disabled state.
 */
export type ErrorRowState = 'clickable' | 'unreachable' | 'unaddressed';

export interface ErrorRowProps {
  label: string;
  /** The caption shown under the title — the message, plus the reason when the
   *  row cannot be reached. */
  caption: string;
  accessibleName: string;
  rowNumber: number | null;
  rowIndex: number | null;
  rowId: string | null;
  state: ErrorRowState;
}

export const buildErrorRowProps = (
  error: WorkflowError,
  {
    resolveRowIndex,
    displayNumberById,
    isRowReachable,
    unreachableHint,
    unknownErrorText,
  }: ErrorRowContext
): ErrorRowProps => {
  const rowNumber = resolveErrorRowNumber(
    error,
    (id) => displayNumberById.get(id) ?? 0
  );
  const label = buildErrorLabel({
    colId: error.colId,
    nodeNumber: error.nodeNumber,
    rowNumber,
  });

  const rowIndex = resolveRowIndex(error);
  const rowId = error.nodeId ?? null;

  // The unreachable check comes FIRST while an id exists: it is the more
  // specific diagnosis and the only one that can name a cause the user can act
  // on. Without an id there is nothing to resolve against, so the pre-existing
  // index gate answers.
  const isUnreachable =
    rowId !== null && !!isRowReachable && !isRowReachable(rowId);

  const state: ErrorRowState = isUnreachable
    ? 'unreachable'
    : isAddressableCell(rowIndex, error.colIndex)
      ? 'clickable'
      : 'unaddressed';

  const caption = [error.errorMessage, isUnreachable ? unreachableHint : null]
    .filter((part): part is string => !!part)
    .join(' ');

  return {
    label,
    caption,
    // The caption, not the raw message: `aria-label` on a host with
    // `role="menuitem"` replaces the shadow content wholesale, so a reason
    // printed only in the caption would never be announced.
    accessibleName: buildErrorAccessibleName(
      label,
      caption || null,
      unknownErrorText
    ),
    rowNumber,
    rowIndex,
    rowId,
    state,
  };
};

/**
 * A stable identity for one rendered error entry — the address it points at
 * plus what it says. Two entries with the same key are the same problem said
 * twice, and only one of them is worth rendering or counting.
 *
 * Deliberately includes `nodeId` and `rowIndex`: the two entries of a duplicate
 * node number carry the SAME message and the same number and differ only by
 * the row they address, and collapsing those would drop click-navigation to the
 * second offending cell — the reason both exist.
 */
export const errorIdentityKey = (error: WorkflowError): string =>
  [
    error.colId ?? '',
    error.nodeId ?? '',
    error.rowIndex ?? '',
    error.nodeNumber ?? '',
    error.cellNumber ?? '',
    error.errorMessage ?? '',
  ].join('|');

/**
 * Which errors each popover column renders, and how many the trigger badge
 * counts (PSTUDIO-3990 review defect 5).
 *
 * A derived node-number error lands in BOTH `effectiveErrors` and
 * `effectivePifErrors` (`table-store.ts` — one derivation feeds both lists), and
 * with `agentCompilerMode === 'on'` the popover shows both columns. So ONE
 * blank `Node #` cell produced a badge reading 2 and two identical entries.
 * That was true for a duplicate before this ticket too, but a blank cell is now
 * reachable in bulk — clearing a column, a CSV import — so the scale changed.
 *
 * Dedup happens HERE, at display level, and nowhere near the store: the eight
 * error indexes the cell highlights are built from read the full per-list
 * errors, so BOTH conflicting cells stay highlighted. Dropping the duplicate
 * from a store list would take a highlight with it.
 *
 * The PIF column wins the shared entry because it renders first (leftmost in
 * the side-by-side layout), so the surviving entry is where the user looks.
 */
export const partitionPopoverErrors = ({
  pifErrors,
  apiErrors,
  showPif,
  showApi,
}: {
  pifErrors: WorkflowError[];
  apiErrors: WorkflowError[];
  showPif: boolean;
  showApi: boolean;
}): { pif: WorkflowError[]; api: WorkflowError[]; total: number } => {
  const pif = showPif ? pifErrors : [];

  if (!showApi) {
    return { pif, api: [], total: pif.length };
  }

  if (!showPif) {
    // Nothing from the PIF list is on screen, so there is nothing to dedup
    // against — the API error is the only copy the user can see.
    return { pif: [], api: apiErrors, total: apiErrors.length };
  }

  const shown = new Set(pif.map(errorIdentityKey));
  const api = apiErrors.filter((error) => !shown.has(errorIdentityKey(error)));

  return { pif, api, total: pif.length + api.length };
};

/** Lines a WINDOWED error row's caption is clamped to, handed to
 *  `ps-graph-node` as `captionLines`. Only windowed rows get it — see the note
 *  on `ERROR_ROW_ESTIMATE`. */
export const ERROR_ROW_CAPTION_LINES = 2;

/** The height of a windowed `variant="flush"` `ps-graph-node` row.
 *
 *  **Not an estimate — a CONTRACT (PSTUDIO-4119).** Nothing measures these
 *  rows: the windowed list carries no measurement ref, so this number is
 *  simultaneously the virtualizer's `estimateSize`, the pinned `height` in
 *  `buildWindowedRowStyle`, and the step between rows.
 *
 *  It holds because a windowed row asks the design system to clamp its caption
 *  to `ERROR_ROW_CAPTION_LINES` and reserve that height either way, so the row
 *  is one height for ANY text:
 *    `padding-block: spacing(medium)` twice        16 + 16
 *    label — `ps-text-body` -> `2xsmall`           line-height 24
 *    `.c-graph-node__header` `margin-block-end`     2
 *    caption — 2 x `4xsmall` line-height 16        32
 *    `border-bottom` on a `role="menuitem"` host    0.5
 *  = 90.5, which lays out as 91.
 *
 *  Why a clamp rather than a bigger number: rows do not share one natural
 *  height. Measured at the popover's own width: a plain message is 75px, a
 *  message plus `error_row.hidden_row_hint` is 91px, and an `invalid node
 *  number` message quoting a pasted value is 107px — as is the Russian-length
 *  rendering. So 68 clipped every row, 91 alone still clipped the three-line
 *  ones, and any next number would clip whatever came after it.
 *
 *  Costs, stated rather than hidden. Text past two lines is not shown in the
 *  row — the ellipsis says so, and the full string stays in the row's `title`
 *  and accessible name, which is a real loss for a sighted keyboard user since
 *  `title` needs a hover. And because the caption is `message + hint`, it is
 *  the HINT that goes first when the message is long — the hint explains why a
 *  click cannot navigate (PSTUDIO-3990 review defect 2), so on such a row that
 *  explanation is only in the accessible name. Both are windowed-only: an
 *  unwindowed row is not clamped at all.
 *
 *  Kept honest by tests rather than by this comment: `graph-node.wc.test.ts`
 *  pins that `captionLines` reaches the caption and that omitting it clamps
 *  nothing, and the e2e asserts a windowed row's rendered height and the height
 *  of its INNER element against this constant. */
export const ERROR_ROW_ESTIMATE = 91;

/** How tall the scroller is allowed to get. The popover itself is capped at
 *  `max-height: 70vh` (`popover.scss`) and scrolls, so a taller list would put
 *  a scroller inside a scroller. */
export const ERROR_LIST_MAX_HEIGHT = 480;

/** Rows kept mounted above and below the window. Deliberately generous: it
 *  costs a handful of elements on a mass error state, and it keeps every
 *  existing e2e — which counts entries for solutions with a handful of errors
 *  — looking at a fully rendered list. */
export const ERROR_LIST_OVERSCAN = 8;

/**
 * Below this many errors the list is NOT windowed at all.
 *
 * Windowing has a real cost that only shows up in assistive tech: `ps-menu`
 * hands out its roving `tabindex` from `getAllItems()`, which reads
 * `slot.assignedElements()` — the DIRECT slotted children. Windowed rows can be
 * direct children too (they are, below), but only the ones currently MOUNTED
 * exist, so arrow-key roving can never walk past the window into rows the
 * virtualizer has not rendered. Every real solution has a handful of errors, so
 * paying that price unconditionally trades a working keyboard on the common
 * case for a fix that only the pathological one needs.
 *
 * So: a list this size or smaller renders in full and behaves exactly as it did
 * before windowing existed. Past it — a blank `Node #` column pasted over
 * thousands of rows — the window is what keeps the popover openable at all, and
 * the roving limit is the accepted cost (TICKETED separately).
 */
export const ERROR_LIST_VIRTUALIZE_THRESHOLD = 150;

/**
 * The geometry of the windowed error list (PSTUDIO-3990 review defect 6).
 *
 * Neither error class is capped in the store, on purpose: the cell highlights
 * are built from the same lists, so a cap would stop highlighting the rows past
 * it while the publish toast still says "fix the highlighted cells". A blank
 * `Node #` column pasted over thousands of rows therefore produces thousands of
 * errors, and the popover mounted one Lit `ps-graph-node` per error — a click
 * on the badge froze the main thread.
 *
 * The list is windowed with `@tanstack/react-virtual`, the SAME virtualizer the
 * workflows table already uses (`components/table/table.tsx` →
 * `useVirtualizer`); no new dependency. This helper holds the arithmetic so it
 * is testable in the node environment, where neither the DOM nor the
 * virtualizer exists.
 *
 * `shouldVirtualize` is the switch itself — see
 * `ERROR_LIST_VIRTUALIZE_THRESHOLD` for why the common case opts out. When it
 * is false the other fields are unused: the list renders in full and grows with
 * its content, exactly as it did before windowing.
 *
 * `totalHeight` is the full height of the windowed list and `height` the
 * scroller's own, capped. Both are ARITHMETIC — `count * rowEstimate` — and
 * that is deliberate (PSTUDIO-4119): the component used to take them from
 * `virtualizer.getTotalSize()`, i.e. from the virtualizer's own measurement
 * output, while the virtualizer put a `ResizeObserver` on that very scroller.
 * A windowed row is now pinned to `rowEstimate` (see `buildWindowedRowStyle`),
 * so these two numbers are exactly what a measured total would be anyway —
 * with no path from the measurement back into the element being measured.
 */
export const getErrorListLayout = ({
  count,
  rowEstimate = ERROR_ROW_ESTIMATE,
  maxHeight = ERROR_LIST_MAX_HEIGHT,
  overscan = ERROR_LIST_OVERSCAN,
  virtualizeThreshold = ERROR_LIST_VIRTUALIZE_THRESHOLD,
}: {
  count: number;
  rowEstimate?: number;
  maxHeight?: number;
  overscan?: number;
  virtualizeThreshold?: number;
}): {
  height: number;
  totalHeight: number;
  rowEstimate: number;
  overscan: number;
  shouldVirtualize: boolean;
} => {
  const totalHeight = count * rowEstimate;

  return {
    height: Math.min(totalHeight, maxHeight),
    totalHeight,
    rowEstimate,
    overscan,
    shouldVirtualize: count > virtualizeThreshold,
  };
};

/**
 * What the validation trigger shows: which glyph, which button variant, and
 * whether a count is painted at all (PSTUDIO-4120).
 *
 * Extracted as a pure function because the component itself is untestable here
 * — it pulls in Lit design-system elements, which crash the node environment —
 * and the two states this ticket adds are precisely the ones an e2e cannot
 * reach: `compiler-degraded` / `threw` on a solution whose error lists are
 * EMPTY. Left inline, both new branches would have been covered by nothing.
 *
 * The count is dropped whenever the pass did not finish, and that is a
 * correctness rule rather than taste: the accessible name in that state reads
 * "did not finish" and deliberately carries no number, so painting one would
 * have the control assert to a sighted user ("4 errors") exactly what it refuses
 * to assert to a screen-reader user — WCAG 2.5.3, and a plain contradiction
 * besides. Whatever the stale list still holds is reachable in the popover.
 */
export type ValidationTriggerGlyph = 'check' | 'warning' | 'error';

export interface ValidationTriggerBadge {
  glyph: ValidationTriggerGlyph;
  /** `null` means: paint no number at all. */
  count: number | null;
  variant: 'ghost' | 'validation-destructive';
}

/**
 * The English text behind `validation_popover.incomplete_state`.
 *
 * One constant because two call sites render this same sentence — the popover
 * banner and the Publish tooltip — and a `defaultValue` is what the user sees
 * whenever the key is missing. Two copies drift the moment the catalog is
 * reworded, and the drift is invisible: both keep rendering, just differently.
 */
export const VALIDATION_INCOMPLETE_FALLBACK =
  'Validation did not finish, so these results may be out of date. Publishing stays disabled until a validation run completes.';

export const resolveValidationTriggerBadge = ({
  compiledClean,
  validationIncomplete,
  badgeCount,
}: {
  compiledClean: boolean;
  validationIncomplete: boolean;
  badgeCount: number;
}): ValidationTriggerBadge => {
  if (validationIncomplete) {
    // Deliberately NOT the `validation-warning` DS variant, tempting as the name
    // is: its background (`rgba(#ffedb4,0.2)`) leaves the amber glyph at 1.78:1
    // — measured over the trigger's REAL backdrop, `color(gray,50)` from
    // `nav-controls.scss`, not over white — and `gold 500` is the darkest amber
    // the palette has. The destructive variant is not chosen for contrast
    // either (see `icon-warning.tsx`: 2.83:1, also short of the 3:1 WCAG 1.4.11
    // asks for). It is chosen for HONESTY — this state BLOCKS publishing, it is
    // not an advisory — and for parity with the error glyph beside it.
    return { glyph: 'warning', count: null, variant: 'validation-destructive' };
  }
  if (compiledClean) {
    return { glyph: 'check', count: null, variant: 'ghost' };
  }
  return {
    glyph: 'error',
    count: badgeCount,
    variant: 'validation-destructive',
  };
};

/**
 * The inline style of ONE windowed error row.
 *
 * Extracted from the component so it can be pinned by a plain node test; the
 * component's own wiring is covered by the jsdom test next to it
 * (`__tests__/error-list.test.tsx`) and by the e2e.
 *
 * **The height is pinned unconditionally (PSTUDIO-4119).** It used to be pinned
 * on Firefox only, and everywhere else the row was measured instead — that
 * measurement is what made the measure → notify → re-render loop reachable: a
 * sub-pixel height that flips between repaints never lets `resizeItem`'s strict
 * `delta !== 0` settle, and React throws #185 (Maximum update depth exceeded),
 * which the app-wide boundary turns into the "Reload Page" screen for the whole
 * Workflows route.
 *
 * With a fixed row height the list needs no measurement ref, and THAT is what
 * makes the loop structurally impossible here rather than merely damped:
 * `observe()` is called only from inside `Virtualizer.measureElement`, so a row
 * that never receives the ref is never observed. (Dropping only the
 * `measureElement` OPTION would not achieve this — `virtual-core`'s
 * `setOptions` deletes undefined keys and substitutes the library's own
 * measurer. The hysteresis in `utils/virtualizer-measure-element.ts` is the
 * damper for the one consumer that still measures, the table.)
 *
 * The cost, stated rather than hidden: a caption taller than
 * `ERROR_ROW_ESTIMATE` is CLIPPED instead of laid over its neighbour, and the
 * full text stays in the row's native `title` and in its accessible name.
 */
export const buildWindowedRowStyle = ({
  start,
  rowEstimate,
}: {
  start: number;
  rowEstimate: number;
}): {
  position: 'absolute';
  insetBlockStart: number;
  insetInlineStart: number;
  width: string;
  transform: string;
  height: number;
  overflow: 'hidden';
} => ({
  position: 'absolute',
  insetBlockStart: 0,
  insetInlineStart: 0,
  width: '100%',
  transform: `translateY(${start}px)`,
  height: rowEstimate,
  overflow: 'hidden',
});

/**
 * The accessible name of the popover's trigger button (PSTUDIO-3990 review
 * defect 7).
 *
 * The trigger is a `ps-button` whose only content is the badge NUMBER — and in
 * the clean state an `<svg>` with no `role` or `aria-label`, i.e. no accessible
 * name at all. `ps-button` now exposes `label` / `expanded` / `hasPopup`, so the
 * app can name it without touching the design system.
 *
 * The two keys are `validation_popover.trigger.label_clean` and
 * `validation_popover.trigger.label_with_errors`, the latter pluralised by
 * i18next's `_one`/`_other` suffixes (JSON v4, the default in i18next 22 —
 * verified against the installed copy). `count: 0` resolves to `_other` in
 * English, which is why the clean state is its OWN key rather than a `_zero`
 * variant: "0 errors" is not what that state should say.
 *
 * PSTUDIO-4120 adds a third state and it takes PRECEDENCE over the count: when
 * the last pass produced no verdict, "no errors" is the one thing the control
 * must not say — an empty list then means "nobody checked", and a screen-reader
 * user gets that claim with nothing on screen to contradict it.
 */
export const buildValidationTriggerLabel = (
  errorCount: number,
  t: TranslateFn,
  { incomplete = false }: { incomplete?: boolean } = {}
): string =>
  incomplete
    ? t('pro-studio/navigation:validation_popover.trigger.label_incomplete', {
        defaultValue: 'Solution validation: did not finish',
      })
    : errorCount <= 0
      ? t('pro-studio/navigation:validation_popover.trigger.label_clean', {
          defaultValue: 'Solution validation: no errors',
        })
      : t(
          'pro-studio/navigation:validation_popover.trigger.label_with_errors',
          {
            count: errorCount,
            defaultValue_one: 'Solution validation: {{count}} error',
            defaultValue: 'Solution validation: {{count}} errors',
          }
        );
